import * as Sentry from '@sentry/angular';

/**
 * How many flags the attribution context carries.
 *
 * **Ten because the Sentry API caps there**, and the cap is applied here rather than left to the SDK:
 * over the limit the SDK truncates, silently and by an ordering nobody chose, so an incident reader
 * cannot tell whether a flag is absent because it was never evaluated or because it lost a race. Capping
 * deliberately keeps the *most recently evaluated* ten — the ones that gated whatever the user was doing
 * when the event fired — and makes the eviction a documented rule instead of an accident.
 *
 * The registry declares two flags today, so this is a ceiling with room, not a constraint anyone is
 * pressed against.
 */
const MAX_ATTRIBUTED_FLAGS = 10;

/** The Sentry context this attribution lives under — one context, so an event carries one flag section. */
const FLAG_ATTRIBUTION_CONTEXT = 'feature_flags';

/**
 * The `flagMetadata` key `degraded` travels on — the reader's half of the contract
 * `FLAG_METADATA_KEY` states in `client-core-feature-flags`' `openfeature/web-provider.ts`, and the same
 * spelling the server plane uses in `@efficiently/server-core-api-common`.
 *
 * **Why a local literal rather than a shared constant.** The writer is in
 * `@efficiently/client-core-feature-flags`; importing it here would make every consumer of the Sentry
 * bootstrap — `client-portal` included, which has no flag consumer at all — depend on the feature-flag
 * library for one string. The drift risk is real and accepted: it is one key, named on both sides, and
 * its failure mode is visible rather than silent, since a mis-spelled key reads as
 * {@link DEGRADED_UNSTATED} on every single event instead of as a healthy `false`.
 */
const DEGRADED_METADATA_KEY = 'degraded';

/**
 * What `degraded` reads as when the metadata did not state a boolean.
 *
 * `'UNKNOWN'` rather than `false`, and the same word the server bridge uses: collapsing *absent* into
 * *healthy* would have an incident reader draw an affirmative claim of health from no evidence at all.
 * Absent metadata means a foreign provider is registered on the OpenFeature API or the provider and this
 * hook have skewed versions — both worth seeing, and neither visible if it looks like a healthy read.
 */
const DEGRADED_UNSTATED = 'UNKNOWN';

/**
 * The stand-in for anything that is not shaped like a registry flag key.
 *
 * **This bound is a PII control, not tidiness (TR-5).** OpenFeature types a flag key as `string`, so what
 * arrives here is verbatim whatever a call site passed to `getBooleanValue`. A call site that *built* one
 * — `getBooleanValue('flag-' + orgId, false)` — would put customer data into a third-party record through
 * the key itself, and the same word the server plane uses (`FLAG_TAG_UNREGISTERED`) is what it collapses
 * to, so an operator reads one vocabulary across both planes.
 *
 * It means something slightly different here, and deliberately: the server checks *registry membership*,
 * this checks {@link REGISTRY_KEY_SHAPE}. Membership would mean importing the generated enum, which would
 * give every application's Sentry bootstrap — `client-portal` included, which has no flag consumer at all
 * — a dependency on the feature-flag library for one lookup. The residual gap is a string that happens to
 * be a bare `PascalCase` word: a numeric or UUID id, an email, and anything interpolated with a separator
 * or a digit-leading segment all fail the shape, and a bare capitalised word carries no customer data
 * worth the coupling.
 */
const UNREGISTERED_FLAG_KEY = 'UNREGISTERED';

/**
 * What a registry flag key looks like: one `PascalCase` word of letters and digits, at most 40 characters.
 *
 * `flags/registry.yaml` keys are code identifiers (`PdfDefects`, `SpecItem`) and `flags:codegen` emits
 * them into a TypeScript enum, so the shape is enforced upstream — this is the reader's end of it. The
 * length cap is what stops an unbounded string from becoming an unbounded Sentry context key even when it
 * happens to be alphanumeric.
 */
const REGISTRY_KEY_SHAPE = /^[A-Z][A-Za-z0-9]{0,39}$/;

/** OpenFeature's `flagMetadata`: whatever the provider chose to state about a resolution. */
type OpenFeatureFlagMetadata = Readonly<Record<string, string | number | boolean>>;

/**
 * The slice of OpenFeature's `HookContext` this hook reads.
 *
 * Typed structurally, exactly as `ScrubbableSentryLog` is in `sentry-log-scrub.util.ts` and for the same
 * reason: it keeps this library — which every application's bootstrap imports — free of a dependency on
 * a flag SDK's types, and free of the SDK itself in any bundle that never registers a provider.
 */
export interface OpenFeatureHookContextView {
	readonly flagKey: string;
	/** What the caller receives when the evaluation fails — the value worth attributing on that path. */
	readonly defaultValue: unknown;
}

/** The slice of OpenFeature's `EvaluationDetails` this hook reads. */
export interface OpenFeatureEvaluationDetailsView {
	readonly flagKey: string;
	readonly value: unknown;
	readonly flagMetadata?: OpenFeatureFlagMetadata;
}

/** The two lifecycle stages this attribution needs: a resolution, and a failure to resolve. */
export interface OpenFeatureAttributionHook {
	after(hookContext: OpenFeatureHookContextView, evaluationDetails: OpenFeatureEvaluationDetailsView): void;
	error(hookContext: OpenFeatureHookContextView, error: unknown): void;
}

/**
 * The OpenFeature global API, as far as this registration is concerned — satisfied by the `OpenFeature`
 * singleton exported from `@openfeature/web-sdk`.
 */
export interface OpenFeatureHookRegistrar {
	addHooks(...hooks: OpenFeatureAttributionHook[]): void;
}

/** One flag's attribution: the boolean it resolved to, and whether that boolean is a fallback. */
interface FlagAttribution {
	readonly enabled: boolean;
	readonly degraded: boolean | typeof DEGRADED_UNSTATED;
}

/**
 * One reading handed to {@link attribute} — an evaluation's outcome before it is bounded and recorded.
 *
 * Grouped into an object rather than passed as three arguments so the two hook stages build the same
 * shape, and so adding a field later does not widen a parameter list.
 */
interface FlagReading {
	readonly key: string;
	/** Unbounded on purpose: {@link attribute} is where a non-boolean is dropped. */
	readonly value: unknown;
	readonly degraded: boolean | typeof DEGRADED_UNSTATED;
}

/** A flag key reduced to a bounded value before it reaches a third-party record — see {@link UNREGISTERED_FLAG_KEY}. */
function toBoundedKey(key: string): string {
	return REGISTRY_KEY_SHAPE.test(key) ? key : UNREGISTERED_FLAG_KEY;
}

/**
 * Narrows the provider's loose metadata back to a boolean, or to {@link DEGRADED_UNSTATED}.
 *
 * A narrowing and never a cast: `flagMetadata` is carried by the SDK without validation, and a
 * wrong-typed value must read as "not stated" rather than lie about its type.
 */
function readDegraded(metadata: OpenFeatureFlagMetadata | undefined): boolean | typeof DEGRADED_UNSTATED {
	const degraded: unknown = metadata?.[DEGRADED_METADATA_KEY];

	return typeof degraded === 'boolean' ? degraded : DEGRADED_UNSTATED;
}

/**
 * Writes the capped attribution onto the Sentry scope, so every later event carries it.
 *
 * **Delete-then-set is the LRU**: re-evaluating a flag moves it to the end of the insertion order, so the
 * oldest entry is always the one at the front and the eviction below drops the least recently evaluated
 * flag rather than an arbitrary one.
 *
 * Nothing here can fail an evaluation: it runs on the flag-read path, and a flag read must never break
 * because attribution could not be recorded. `setContext` on a scope with no client is a no-op, which
 * is what makes the "Sentry is disabled locally" case free rather than special.
 */
function attribute(attributed: Map<string, FlagAttribution>, reading: FlagReading): void {
	if (typeof reading.value !== 'boolean') {
		return;
	}

	const key = toBoundedKey(reading.key);

	attributed.delete(key);
	attributed.set(key, { enabled: reading.value, degraded: reading.degraded });

	while (attributed.size > MAX_ATTRIBUTED_FLAGS) {
		const oldest = attributed.keys().next();

		if (oldest.done) {
			break;
		}

		attributed.delete(oldest.value);
	}

	Sentry.setContext(FLAG_ATTRIBUTION_CONTEXT, Object.fromEntries(attributed));
}

/**
 * The hook that makes `degraded` visible on a Sentry event — the half Sentry's own hook does not carry.
 *
 * `openFeatureIntegration()` plus `OpenFeatureIntegrationHook` give Sentry's Feature Flags view the flag
 * and its boolean. Neither carries provenance, and the boolean alone cannot answer the only question an
 * incident actually asks: *is this feature off because the organisation does not have it, or because a
 * rung failed and we served the default?* Those are a support answer and an outage, and they look
 * identical without this.
 *
 * State lives in the closure rather than at module scope, so the cap is per registration: a test gets a
 * fresh buffer, and nothing survives between two applications sharing a bundle.
 *
 * **No PII, by construction (TR-5).** Two fields per entry — a boolean, and a boolean or the unstated
 * placeholder — under a key bounded by {@link REGISTRY_KEY_SHAPE}: no user id, no organisation, no email,
 * and no free-form string from a call site.
 */
function createFlagAttributionHook(): OpenFeatureAttributionHook {
	const attributed = new Map<string, FlagAttribution>();

	return {
		after(_hookContext: OpenFeatureHookContextView, evaluationDetails: OpenFeatureEvaluationDetailsView): void {
			attribute(attributed, {
				key: evaluationDetails.flagKey,
				value: evaluationDetails.value,
				degraded: readDegraded(evaluationDetails.flagMetadata),
			});
		},
		/**
		 * An evaluation that threw served the caller's default, which is degraded by definition — the
		 * value the user got did not come from a resolution at all. The error itself is not recorded
		 * here: Sentry captures it through its own channels, and a message can carry anything.
		 */
		error(hookContext: OpenFeatureHookContextView, _error: unknown): void {
			attribute(attributed, { key: hookContext.flagKey, value: hookContext.defaultValue, degraded: true });
		},
	};
}

/**
 * Registers flag attribution on Sentry — **both** halves, because one without the other ships blank
 * (BR-4).
 *
 * 1. `openFeatureIntegration()` is what copies the recorded flags onto an outgoing event. Without it the
 *    hooks record into a scope nothing ever reads.
 * 2. `OpenFeature.addHooks(...)` is what records them. Without it the integration attaches an empty
 *    list. The `openFeature` argument **is** that singleton — `OpenFeature` from `@openfeature/web-sdk`,
 *    passed in rather than imported so this library keeps no dependency on a flag SDK (see
 *    {@link OpenFeatureHookContextView}).
 *
 * Neither omission raises anything: no error, no log, just an attribution field nobody notices is
 * missing until the incident that needed it. That is why the two calls sit in one function that either
 * runs or does not, and why the spec asserts them separately.
 *
 * Called once, from `initClientSentry`, and only after `Sentry.init` — `addIntegration` needs a client
 * to attach to, and hooks recorded before one exists would land on a scope that is thrown away.
 */
export function registerOpenFeatureSentryAttribution(openFeature: OpenFeatureHookRegistrar): void {
	Sentry.addIntegration(Sentry.openFeatureIntegration());

	openFeature.addHooks(new Sentry.OpenFeatureIntegrationHook(), createFlagAttributionHook());
}
